import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { useRegion } from 'common/hooks/region';
import { getFormatedShiftDate } from '../../../../services/helpers/shift';
import { NextAppointmentsService } from './services/next-appointments.service';
import {
  SHIFTS_REF,
  everportOakTerminalKey,
  everportTerminalKey,
} from './constants/next-appointments.constants';
import { ShowHideNextShifts } from './ShowHideNextShifts';
import { HeaderCellTag } from './HeaderCellTag';
import {
  AppointmentCell,
  ITSCell,
  NoAppointmentCell,
  DoNotDisplayAppointmentCell,
} from './components/cells';
import { CheckAppointmentsPortalCell } from './components/cells/CheckAppointmentsPortalCell';
import { doNotDisplayRule } from 'services/clients/terminal';
import { ErlContext } from 'pages/T_Appointments/context';

const NextAppointments = ({
  classes,
  terminals,
  currentSLKey,
  currentContainerKey,
  showCurrentShiftOnly,
  forDatetime,
}) => {
  const region = useRegion();
  const erlData = useContext(ErlContext);
  const availableAppointments = erlData.appointments;

  const [isAllShiftsShown, toggleShowHideAllShifts] = useState(false);

  const data = erlData.availabilities;
  const dataBySL = data[currentSLKey];
  const dataBySlAndContainer = dataBySL && dataBySL[currentContainerKey];

  if (!terminals || !availableAppointments) {
    return <div>Loading...</div>;
  }

  const keys = terminals.map((item) => item.key);

  const availableAppointmentRestructured = Object.keys(availableAppointments).map((key) => ({
    terminal: key,
    ...availableAppointments[key],
  }));

  if (keys.includes(everportTerminalKey)) {
    availableAppointmentRestructured.push({ terminal: everportTerminalKey });
  } else if (keys.includes(everportOakTerminalKey)) {
    availableAppointmentRestructured.push({ terminal: everportOakTerminalKey });
  }

  const filteredAppointments = availableAppointmentRestructured.filter((item) =>
    keys.includes(item.terminal)
  );

  const createRows = () => {
    if (!terminals && !availableAppointments) return;

    let isFirstTagRendered = false;
    let availableShifts = NextAppointmentsService.createShiftsList(
      filteredAppointments,
      currentContainerKey,
      currentSLKey,
      region
    );

    let shiftCount = region.erlForecastDays * Math.min(region.shifts.length, 2);

    if (showCurrentShiftOnly) {
      shiftCount = 1;
    } else if (isAllShiftsShown) {
      shiftCount = availableShifts.length;
    }

    const renderedRows = availableShifts.slice(0, shiftCount);

    const result = renderedRows.map((item, i) => {
      return createRow(item, isFirstTagRendered, () => {
        isFirstTagRendered = true;
      });
    });

    // Potentially add show / hide more button
    if (region.code === 'USLA') {
      if (isAllShiftsShown) {
        result.splice(
          region.erlForecastDays * region.shifts.length,
          0,
          <ShowHideNextShifts
            key={Math.random()}
            classes={classes}
            terminalsLength={terminals.length + 1}
            isAllShiftsShown={isAllShiftsShown}
            toggleShowHideAllShifts={toggleShowHideAllShifts}
          />
        );
      } else {
        result.push(
          <ShowHideNextShifts
            key={Math.random()}
            classes={classes}
            terminalsLength={terminals.length + 1}
            isAllShiftsShown={isAllShiftsShown}
            toggleShowHideAllShifts={toggleShowHideAllShifts}
          />
        );
      }
    }

    return result;
  };

  const createRow = (shift, isFirstTagRendered, cb) => {
    if (shift.shift === SHIFTS_REF.shift2) {
      return createRowWith(classes.fatRow, shift, isFirstTagRendered, cb);
    }
    return createRowWith(classes.row, shift, isFirstTagRendered, cb);
  };

  const createRowWith = (classes, shift, isFirstTagRendered, cb) => {
    return (
      <TableRow key={Math.random()} className={classes}>
        {createHeaderCell(shift, isFirstTagRendered, cb)}
        {createCells(shift)}
      </TableRow>
    );
  };

  const createHeaderCell = (shift, isFirstTagRendered, cb) => {
    const cells = terminals.map((item, index) => {
      const terminalAvailability = dataBySlAndContainer && dataBySlAndContainer[item.key];

      const { availability } = terminalAvailability || {};

      const shiftsByDay = availability && availability[shift.day];
      const instructions = shiftsByDay && shiftsByDay[shift.shift];
      const cellData = {
        ...item,
        instructions,
      };

      return createCell(cellData, shift, index, true);
    });

    const availableCell = cells.find(
      (cell) =>
        cell &&
        cell.instructions &&
        (cell.instructions.status === 'yes' || cell.instructions.status === 'dual')
    );

    if (availableCell && !isFirstTagRendered) {
      cb();
    }

    const shiftName = shift.shift === SHIFTS_REF.shift1 ? 'shift 1' : 'shift 2';

    // BCIC-179 Add a tag to the shifts of the current day (in the row name cell)
    const isShiftToday = NextAppointmentsService.checkIsShiftToday(shift.day);

    return (
      <TableCell key={Math.random()} className={classes.headerCell1} align="center">
        {isShiftToday && <HeaderCellTag classes={classes} text="TODAY" />}
        {availableCell && !isFirstTagRendered && <HeaderCellTag classes={classes} text="FIRST" />}
        {getFormatedShiftDate(shift.day)}
        {region.shifts.length > 1 ? (
          <>
            <br />
            <b>{shiftName.toUpperCase()}</b>
          </>
        ) : null}
      </TableCell>
    );
  };

  const createCells = (shift) => {
    const cells = terminals.map((item, index) => {
      const terminalAvailability = dataBySlAndContainer && dataBySlAndContainer[item.key];
      const { availability } = terminalAvailability || {};
      const shiftsByDay = availability && availability[shift.day];
      const instructions = shiftsByDay && shiftsByDay[shift.shift];
      const cellData = {
        ...item,
        instructions,
      };
      return createCell(cellData, shift, index);
    });
    return cells;
  };

  const createCell = (terminal, shift, index, isHeader) => {
    const { terminal_portal, instructions } = terminal;
    const key = terminal.key + shift.day + shift.shift + index;

    const currentTerminal = filteredAppointments.find((item) => item.terminal === terminal.key);

    const isPTSC = NextAppointmentsService.checkIsPtscTerminal(terminal);

    const isMatson = NextAppointmentsService.checkIsMatsonTerminal(terminal);

    if (isPTSC || isMatson) {
      if (isHeader)
        return {
          shift,
          terminalKey: key,
          instructions,
        };

      return (
        <ITSCell
          key={key}
          shift={shift}
          terminalKey={terminal.key}
          terminalUrl={terminal_portal}
          instructions={instructions}
          classes={classes}
          isPTSC={isPTSC}
          currentContainerKey={currentContainerKey}
          currentSLKey={currentSLKey}
        />
      );
    }

    const doNotDisplayAppointments = terminal.appointment_rules.includes(doNotDisplayRule);
    if (doNotDisplayAppointments) {
      if (isHeader)
        return {
          shift,
          terminalKey: key,
          instructions,
        };

      return (
        <DoNotDisplayAppointmentCell
          key={key}
          terminalKey={terminal.key}
          shift={shift}
          terminalUrl={terminal_portal}
          instructions={instructions}
          classes={classes}
          currentContainerKey={currentContainerKey}
          currentSLKey={currentSLKey}
        />
      );
    }

    // BCIC-190 Add a link "Check appointments portal" where the list of available appointments is missing
    const isTerminalDataNotCorrect = NextAppointmentsService.checkIsTerminalDataNotCorrect(
      currentTerminal,
      currentSLKey,
      terminal,
      forDatetime,
      region.timeZone
    );
    if (isTerminalDataNotCorrect) {
      return (
        <CheckAppointmentsPortalCell
          key={key}
          shift={shift}
          terminalKey={terminal.key}
          instructions={instructions}
          classes={classes}
          currentContainerKey={currentContainerKey}
        />
      );
    }

    // BCIC-404 For FMS, YTI, and TTI, convert appointments to "no appointments available" when empty restriction=CLOSED
    // BCIC-709 Remove logic "no appoitnment" for FMS and YTI, and apply to APM 20.10.2020
    const isApmTti = NextAppointmentsService.checkIsApmTti(terminal.key);
    if (isApmTti && instructions && instructions.status === 'no') {
      return (
        <NoAppointmentCell
          key={key}
          shift={shift}
          terminal={terminal}
          instructions={instructions}
          classes={classes}
          currentContainerKey={currentContainerKey}
        />
      );
    }

    let currentDay = null;

    if (currentTerminal) {
      if (currentTerminal[currentSLKey] && currentTerminal[currentSLKey][currentContainerKey]) {
        currentDay = currentTerminal[currentSLKey][currentContainerKey].availability.find(
          (item) => item[shift.day]
        );
      } else {
        currentDay = null;
      }
    } else {
      return null;
    }

    if (currentDay) {
      const shiftsForDay = currentDay[shift.day];

      if (shift.shift === SHIFTS_REF.shift1) {
        if (
          shiftsForDay &&
          shiftsForDay.shift1 &&
          shiftsForDay.shift1.total_appointments &&
          shiftsForDay.shift1.total_appointments === 0 &&
          shiftsForDay.shift1.slots &&
          shiftsForDay.shift1.slots.length === 0
        ) {
          return (
            <NoAppointmentCell
              key={key}
              shift={shift}
              terminal={terminal}
              instructions={instructions}
              classes={classes}
              currentContainerKey={currentContainerKey}
            />
          );
        }
        return createAppointmentCell(
          shiftsForDay.shift1,
          key,
          terminal_portal,
          shift,
          terminal,
          instructions,
          isHeader
        );
      }
      if (shift.shift === SHIFTS_REF.shift2) {
        if (
          shiftsForDay &&
          shiftsForDay.shift2 &&
          shiftsForDay.shift2.total_appointments === 0 &&
          shiftsForDay.shift2.slots.length === 0
        ) {
          return (
            <NoAppointmentCell
              key={key}
              shift={shift}
              terminal={terminal}
              instructions={instructions}
              classes={classes}
              currentContainerKey={currentContainerKey}
            />
          );
        }
        return createAppointmentCell(
          shiftsForDay.shift2,
          key,
          terminal_portal,
          shift,
          terminal,
          instructions,
          isHeader
        );
      }
      return (
        <NoAppointmentCell
          key={key}
          shift={shift}
          terminal={terminal}
          instructions={instructions}
          classes={classes}
          currentContainerKey={currentContainerKey}
        />
      );
    }
    return (
      <NoAppointmentCell
        key={key}
        shift={shift}
        terminal={terminal}
        instructions={instructions}
        classes={classes}
        currentContainerKey={currentContainerKey}
      />
    );
  };

  const createAppointmentCell = (
    shiftInfo,
    key,
    terminalUrl,
    shift,
    terminal,
    instructions,
    isHeader
  ) => {
    const { status, warnings, chassis_restrictions } = instructions || {};

    if (shiftInfo && shiftInfo.slots && shiftInfo.slots.length && shiftInfo.slots[0]) {
      const { total_appointments } = shiftInfo || {};

      if (isHeader)
        return {
          shift,
          terminalKey: key,
          instructions,
        };

      return (
        <AppointmentCell
          key={key}
          terminalKey={terminal.key}
          terminalUrl={terminalUrl}
          shift={shift}
          status={status}
          warnings={warnings}
          chassis_restrictions={chassis_restrictions}
          appointments={total_appointments}
          start_time={shiftInfo.slots[0].start_time}
          end_time={shiftInfo.slots[0].end_time}
          classes={classes}
          currentSLKey={currentSLKey}
          currentContainerKey={currentContainerKey}
        />
      );
    }
    return (
      <NoAppointmentCell
        key={key}
        shift={shift}
        terminal={terminal}
        instructions={instructions}
        classes={classes}
        currentContainerKey={currentContainerKey}
      />
    );
  };

  return (
    <Table className={classes.table} aria-label="next appointment table">
      <TableBody>{createRows()}</TableBody>
    </Table>
  );
};

NextAppointments.propTypes = {
  user: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  terminals: PropTypes.array.isRequired,
  currentSLKey: PropTypes.string.isRequired,
  currentContainerKey: PropTypes.string.isRequired,
  availabilityCredentials: PropTypes.object.isRequired,
};

export default NextAppointments;
